import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/SendOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { createCampaign, CreateCampaignPayload, listCampaigns, sendCampaign } from '../../api/email';
import { useAuth } from '../../context/authContext';
import { PageHeader } from '../../components/PageHeader';
import { CreateCampaignDialog } from './CreateCampaignDialog';

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  DRAFT: 'default',
  SENDING: 'info',
  SENT: 'success',
  FAILED: 'error',
};

export const CampaignsPage = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "+ New" menu bug fix (QuickCreateButton navigates here with
  // {quickCreate: true}) — this never auto-opened Create Campaign before;
  // the menu item just landed on this list page with no dialog, leaving
  // the user to find and click "New campaign" themselves. Depends on
  // location.state (not `[]`) so it also fires when the user is already
  // on /campaigns and clicks "+ New -> New Campaign" again — see
  // LeadsListPage.tsx's identical fix for the full explanation.
  useEffect(() => {
    if ((location.state as { quickCreate?: boolean } | null)?.quickCreate) {
      setCreateOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const campaignsQuery = useQuery({ queryKey: ['campaigns'], queryFn: listCampaigns });

  const createMutation = useMutation({
    mutationFn: (payload: CreateCampaignPayload) => createCampaign(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setCreateOpen(false);
      setError(null);
    },
    onError: (err) =>
      setError(isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to create campaign') : 'Failed'),
  });

  const sendMutation = useMutation({
    mutationFn: (id: number) => sendCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={EmailOutlinedIcon}
        color="#7F56D9"
        title="Email Campaigns"
        subtitle="Create and send bulk email campaigns to students and leads."
        actions={
          hasPermission('email', 'create') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New campaign
            </Button>
          )
        }
      />

      <Stack spacing={2}>
        {campaignsQuery.data?.map((c) => (
          <Card key={c.id} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">{c.name}</Typography>
                    <Chip size="small" label={c.status} color={STATUS_COLOR[c.status]} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Template: {c.template.name} · Created by {c.createdBy.firstName} {c.createdBy.lastName}
                  </Typography>
                  {c.status !== 'DRAFT' && (
                    <Typography variant="caption" color="text.secondary">
                      {c.sentCount} sent · {c.failedCount} failed · {c.totalRecipients} total recipients
                    </Typography>
                  )}
                </Box>
                {c.status === 'DRAFT' && hasPermission('email', 'send') && (
                  <Button
                    size="small"
                    startIcon={<SendIcon />}
                    variant="outlined"
                    disabled={sendMutation.isPending}
                    onClick={() => sendMutation.mutate(c.id)}
                  >
                    Send now
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
        {campaignsQuery.data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No campaigns yet.
          </Typography>
        )}
      </Stack>

      <CreateCampaignDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        errorMessage={error}
        onSubmit={async (payload) => {
          await createMutation.mutateAsync(payload);
        }}
      />
    </Box>
  );
};
